import { fetchOptions } from '@edsn/api-idm-bff';
import { oidcSpa } from 'oidc-spa/react-spa';
import { z } from 'zod';
import { getConfig } from './utils/config';

export const {
  bootstrapOidc,
  useOidc,
  getOidc,
  enforceLogin,
  // Wrap your all application within this component in src/main.tsx
  // Non blocking rendering is possible, see: https://docs.oidc-spa.dev/v/v10/features/non-blocking-rendering#react-spas
  OidcInitializationGate,
} = oidcSpa
  .withExpectedDecodedIdTokenShape({
    // Describe the expected shape of the ID Token.
    // Think of `decodedIdToken` as your “user” object.
    // If you’re unsure what fields are available, open the console:
    // oidc-spa will log the decoded token for you.
    decodedIdTokenSchema: z.object({
      sub: z.string(),
      roles: z.array(z.string()),
      authz_roles: z.array(
        z.enum(['helpdesk', 'helpdesk-readonly', 'role-assigner']),
      ),

      // User profile
      email: z.string(),
      given_name: z.string(),
      family_name: z.string(),
      name: z.string(),

      // Identity and organization information (partial shape based on libs/auth/src/types/userinfo.ts)
      identity: z.object({
        current: z.string(),
        external: z.object({
          car: z.string().optional().nullable(),
        }),
        assigned: z.record(
          z.string(),
          z.object({
            name: z.string(),
            type: z.union([
              z.literal('organization'),
              z.literal('marketparticipant'),
              z.literal('subunit'),
            ]),
            id: z.string(),
            externalId: z.string(),
            marketRole: z.string().optional().nullable(),
            status: z.string().optional().nullable(),
            ean13: z.string().optional().nullable(),
            registrationNumber: z.string().optional().nullable(),
          }),
        ),
      }),
    }),
  })
  .createUtils();

const config = getConfig();
const shouldEnableDebugLogs =
  globalThis.localStorage?.getItem('oidc-debug-logs') === 'true';

/**
 * This can be called immediately of after you've fetched some remote params.
 * If you call this more than once the subsequent calls will be ignored.
 */
bootstrapOidc({
  implementation: 'real',
  issuerUri: config.idpUrl,
  clientId: config.clientId,
  debugLogs: shouldEnableDebugLogs,
  sessionRestorationMethod: 'full page redirect',
  scopes: [
    'openid',
    'profile',
    'email',
    'roles',
    'fr:idm:*',
    'token_v2',
    'edsn',
    'assigned_organization_roles',
  ],
});

fetchOptions.getToken = async (): Promise<string> => {
  const oidc = await getOidc();
  if (oidc.isUserLoggedIn) {
    return oidc.getAccessToken();
  }

  throw new Error('User is not logged in');
};

fetchOptions.baseUrl = config.apiUrl;
